import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { techniciansApi } from '@/services/technicians'
import { DEFAULT_PAGE_SIZE } from '@/lib/listFetch'
import type {
  TechnicianListQuery,
  CreateTechnicianRequest,
  EditTechnicianRequest,
  ListQuery,
} from '@/types'

export const TECHNICIAN_DETAIL_TAB_STALE_TIME = 30_000
export const TECHNICIAN_DETAIL_DOCUMENTS_QUERY = {
  page: 1,
  limit: DEFAULT_PAGE_SIZE,
  search: undefined,
} satisfies ListQuery

export function useTechniciansList(q: TechnicianListQuery) {
  return useQuery({
    queryKey: ['technicians', 'list', q],
    queryFn: () => techniciansApi.list(q),
  })
}

export function useTechnician(id: string | undefined) {
  return useQuery({
    queryKey: ['technicians', 'detail', id],
    queryFn: () => techniciansApi.get(id as string),
    enabled: !!id,
  })
}

export function useCreateTechnician() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateTechnicianRequest) => techniciansApi.create(body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['technicians', 'list'] }),
  })
}

export function useUpdateTechnician(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EditTechnicianRequest) =>
      techniciansApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['technicians', 'list'] })
      qc.invalidateQueries({ queryKey: ['technicians', 'detail', id] })
    },
  })
}

export function useDeactivateTechnician() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => techniciansApi.deactivate(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['technicians', 'list'] })
      qc.invalidateQueries({ queryKey: ['technicians', 'detail', id] })
    },
  })
}

export function useActivateTechnician() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => techniciansApi.activate(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['technicians', 'list'] })
      qc.invalidateQueries({ queryKey: ['technicians', 'detail', id] })
    },
  })
}

export function useResendTechnicianWelcomeEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => techniciansApi.resendWelcomeEmail(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['technicians', 'detail', id] })
    },
  })
}

export function useDeleteTechnician() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => techniciansApi.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['technicians', 'list'] }),
  })
}

export function technicianJobsQueryOptions(
  id: string,
  q: ListQuery,
  staleTime = 0
) {
  return queryOptions({
    queryKey: ['technicians', 'jobs', id, q],
    queryFn: () => techniciansApi.jobs(id, q),
    staleTime,
  })
}

export function useTechnicianJobs(
  id: string | undefined,
  q: ListQuery,
  options?: { enabled?: boolean; staleTime?: number }
) {
  return useQuery({
    ...technicianJobsQueryOptions(id as string, q, options?.staleTime),
    enabled: !!id && (options?.enabled ?? true),
  })
}

export function useTechnicianAvailability(
  id: string | undefined,
  q: ListQuery
) {
  return useQuery({
    queryKey: ['technicians', 'availability', id, q],
    queryFn: () => techniciansApi.availability(id as string, q),
    enabled: !!id,
  })
}

export function useAddTechnicianNote(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (note: string) => techniciansApi.addNote(id, note),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['technicians', 'detail', id] }),
  })
}

// Options of the technician list "Skills" filter: every service category defined
// in Settings (2.11.5), including those with no catalogue item yet — which is
// why this reads the technician-scoped projection.
export function useTechnicianSkillOptions() {
  return useQuery({
    queryKey: ['technicians', 'skill-options'],
    queryFn: async () => {
      const res = await techniciansApi.serviceCategoryOptions()
      return (res.data ?? []).map(c => c.name)
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function technicianDocumentsQueryOptions(
  id: string,
  q: ListQuery,
  staleTime = 0
) {
  return queryOptions({
    queryKey: ['technicians', id, 'documents', q],
    queryFn: () => techniciansApi.documents(id, q),
    staleTime,
  })
}

export function useTechnicianDocuments(
  id: string | undefined,
  q: ListQuery,
  options?: { enabled?: boolean; staleTime?: number }
) {
  return useQuery({
    ...technicianDocumentsQueryOptions(id as string, q, options?.staleTime),
    enabled: !!id && (options?.enabled ?? true),
  })
}
